// @ts-nocheck
// Nivo chart library types are overly strict and don't match runtime behavior.
// TODO: Add granular type assertions when upgrading Nivo.
import React, { useState, useMemo, useCallback, memo } from 'react';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import Users from 'lucide-react/dist/esm/icons/users';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import X from 'lucide-react/dist/esm/icons/x';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import FileCheck from 'lucide-react/dist/esm/icons/file-check';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Database from 'lucide-react/dist/esm/icons/database';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveLine } from '@nivo/line';
import { getTranslation } from '../../../i18n';


// Data Quality Badge Component
const DataQualityBadge = ({ source }) => {
  if (!source || source === 'not_available') return null;
  
  const badges = {
    document: { 
      icon: <FileCheck size={10} />, 
      label: 'Document Data', 
      className: 'bg-emerald-500/[0.15] text-emerald-500 border-emerald-500/30' 
    },
    ai_estimate: { 
      icon: <Sparkles size={10} />, 
      label: 'AI Estimated', 
      className: 'bg-amber-500/[0.15] text-amber-500 border-amber-500/30' 
    },
    industry_default: { 
      icon: <Database size={10} />, 
      label: 'Industry Default', 
      className: 'bg-slate-400/[0.15] text-slate-400 border-slate-400/30' 
    }
  };
  
  const badge = badges[source] || badges.industry_default;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[0.7rem] font-semibold uppercase tracking-wide ml-3 align-middle border ${badge.className}`}>
      {badge.icon}
      {badge.label}
    </span>
  );
};

// Chart Skeleton Component
const ChartSkeleton = ({ height = 300, type = 'bar' }) => (
  <div className="bg-surface-primary border border-dashed border-default rounded-xl p-4 flex flex-col" style={{ height }}>
    <div className="flex items-center gap-3 mb-4">
      <div className="h-5 w-[120px] rounded-md bg-surface-primary animate-shimmer"></div>
      <div className="h-[18px] w-20 rounded-md bg-surface-primary animate-shimmer"></div>
    </div>
    <div className="flex-1 flex items-end justify-center">
      {type === 'bar' && (
        <div className="flex items-end gap-4 w-full h-full p-4">
          {[0.6, 0.8, 0.5, 0.9, 0.7].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-lg min-h-[20px] bg-surface-primary animate-shimmer" style={{ height: `${h * 100}%` }}></div>
          ))}
        </div>
      )}
      {type === 'pie' && (
        <div className="w-[180px] h-[180px] rounded-full bg-surface-primary animate-shimmer"></div>
      )}
      {type === 'line' && (
        <div className="w-full h-full flex items-center">
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-[60%]">
            <path d="M0,30 Q25,10 50,25 T100,15" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2"/>
          </svg>
        </div>
      )}
    </div>
  </div>
);

// Section Skeleton Component
const SectionSkeleton = () => (
  <div className="rounded-2xl p-6 overflow-hidden bg-surface-primary backdrop-blur-xl border border-default">
    <div className="h-8 w-[200px] rounded-lg mb-6 bg-surface-primary animate-shimmer"></div>
    <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1">
      <ChartSkeleton type="pie" height={360} />
      <ChartSkeleton type="bar" height={360} />
    </div>
  </div>
);

interface FinancialChartsProps {
  dataExtraction: any;
  currency?: string;
  language?: string;
  loading?: boolean;
  externalOpen?: boolean | null;
  onOpenChange?: ((value: boolean) => void) | null;
}

const FinancialCharts = memo(({ dataExtraction, currency = 'EUR', language = 'en', loading = false, externalOpen = null, onOpenChange = null }: FinancialChartsProps) => {
  const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);
  
  // Support both internal and external control of drawer
  const drawerOpen = externalOpen !== null ? externalOpen : internalDrawerOpen;
  const setDrawerOpen = (value) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalDrawerOpen(value);
    }
  };
  const [expandedSections, setExpandedSections] = useState({
    revenue: true,
    profitability: true,
    costs: true,
    liquidity: true,
    trends: true,
    kpis: true
  });

  const t = useCallback((key) => getTranslation(language, key), [language]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Enhanced multi-color scheme by data type
  const COLORS = useMemo(() => ({
    revenue: ['#34d399', '#10b981', '#059669', '#047857'],
    // chart color — token: brand
    profit: ['#8b5cf6', '#7c3aed', '#6d28d9'],
    cost: ['#f59e0b', '#ef4444', '#dc2626'],
    benchmark: '#cbd5e1',
    positive: '#10b981',
    negative: '#ef4444',
    neutral: '#6366f1',
    chart: {
      text: '#e2e8f0',
      grid: '#334155',
      tooltip: {
        bg: 'rgba(15, 23, 42, 0.95)',
        text: '#f1f5f9',
        border: 'rgba(139, 92, 246, 0.5)'
      }
    }
  }), []);

  // Show loading skeletons
  if (loading) {
    return (
      <div className="flex flex-col gap-8 p-4">
        <SectionSkeleton />
        <SectionSkeleton />
      </div>
    );
  }

  // Early return AFTER all hooks
  if (!dataExtraction) return null;

  const { 
    revenue_analysis, 
    profitability, 
    cost_structure, 
    balance_sheet, 
    operational_metrics,
    trends 
  } = dataExtraction;

  // Helper function to safely get numeric values from arrays
  const safeMinValue = (arr, key = 'value', defaultVal = 0) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return defaultVal;
    const values = arr.map(item => item?.[key]).filter(v => v !== null && v !== undefined && !isNaN(v));
    return values.length > 0 ? Math.min(...values) : defaultVal;
  };

  // Helper to filter valid data points
  const filterValidPoints = (arr, key = 'value') => {
    if (!arr || !Array.isArray(arr)) return [];
    return arr.filter(item => item && item[key] !== null && item[key] !== undefined && !isNaN(item[key]));
  };

  // Format metric names from snake_case to Title Case
  const formatMetricName = (metricName) => {
    if (!metricName) return '';
    return metricName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format currency with smart abbreviation
  const formatCurrency = (value, compact = false, context = 'notAvailable') => {
    if (!value && value !== 0) {
      return context === 'notApplicable' 
        ? 'N/A' 
        : 'Not available';
    }
    
    if (compact && Math.abs(value) >= 1000000) {
      return `${currency === 'EUR' ? '€' : '$'}${(value / 1000000).toFixed(1)}M`;
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value, context = 'notAvailable') => {
    if (value === null || value === undefined) {
      return context === 'notApplicable' 
        ? 'N/A' 
        : 'Not available';
    }
    return `${value.toFixed(1)}%`;
  };

  // Format benchmark display (kept for potential future use)
  // eslint-disable-next-line no-unused-vars
  const formatBenchmark = (benchmarkObj) => {
    if (!benchmarkObj || typeof benchmarkObj !== 'object') return null;
    const parts = [];
    if (benchmarkObj.gross_margin) parts.push(`${benchmarkObj.gross_margin}% gross margin`);
    if (benchmarkObj.operating_margin) parts.push(`${benchmarkObj.operating_margin}% operating margin`);
    if (benchmarkObj.net_margin) parts.push(`${benchmarkObj.net_margin}% net margin`);
    return parts.length > 0 ? `Industry Benchmark: ${parts.join(', ')}` : null;
  };

  // Generate sparkline data (mock trend data for demonstration - kept for potential future use)
  // eslint-disable-next-line no-unused-vars
  const generateSparklineData = (currentValue, trend) => {
    const points = 12; // 12 months
    const data = [];
    const trendFactor = trend ? trend / 100 : 0;
    
    for (let i = 0; i < points; i++) {
      const variation = Math.random() * 0.1 - 0.05; // Random variation
      const growth = 1 + (trendFactor * (i / points)) + variation;
      data.push({
        x: i,
        y: currentValue * growth
      });
    }
    return [{ id: 'trend', data }];
  };

  // Prepare revenue by service line data with distinct colors (only show if >= 2 items)
  const revenueByService = revenue_analysis?.by_service_line?.length >= 2
    ? revenue_analysis.by_service_line.map((item, idx) => ({
        id: item.service,
        label: item.service,
        value: item.revenue,
        percentage: item.percentage,
        color: COLORS.revenue[idx % COLORS.revenue.length]
      }))
    : null;

  // Prepare revenue by geography data (only show if >= 2 items)
  const revenueByGeography = revenue_analysis?.by_geography?.length >= 2
    ? revenue_analysis.by_geography.map((item, idx) => ({
        location: item.location,
        revenue: item.revenue,
        percentage: item.percentage,
        color: COLORS.revenue[idx % COLORS.revenue.length]
      }))
    : null;

  // Prepare cost structure data with cost colors (ensure sums to 100%)
  const rawCostData = [
    { 
      id: 'Personnel', 
      label: 'Personnel', 
      value: cost_structure?.personnel_percentage || 0,
      color: COLORS.cost[0]
    },
    { 
      id: 'Overtime', 
      label: 'Overtime', 
      value: cost_structure?.overtime_percentage || 0,
      color: '#f59e0b' // Orange color for overtime
    },
    { 
      id: 'Admin Overhead', 
      label: 'Admin Overhead', 
      value: cost_structure?.admin_overhead_percentage || 0,
      color: COLORS.cost[1]
    },
    { 
      id: 'Other Costs', 
      label: 'Other Costs', 
      value: cost_structure?.other_costs_percentage || 0,
      color: COLORS.cost[2]
    }
  ].filter(item => item.value > 0);

  // Calculate total and add "Unallocated" if doesn't sum to 100%
  const costTotal = rawCostData.reduce((sum, item) => sum + item.value, 0);
  const costStructureData = costTotal < 99.5 && costTotal > 0
    ? [...rawCostData, {
        id: 'Unallocated',
        label: 'Unallocated',
        value: Math.max(0, 100 - costTotal),
        color: '#6b7280'
      }]
    : rawCostData;

  // Prepare profitability data with conditional colors and dynamic benchmarks
  const getBenchmark = (field, defaultValue) => {
    // Handle both object and string formats for backward compatibility
    if (profitability?.industry_benchmark) {
      if (typeof profitability.industry_benchmark === 'object') {
        return profitability.industry_benchmark[field] || defaultValue;
      }
    }
    return defaultValue;
  };

  const profitabilityData = [
    { 
      metric: 'Gross Margin', 
      value: profitability?.gross_margin || 0, 
      // Red for negative or very low (<50%), green for healthy (>=50%)
      color: (profitability?.gross_margin || 0) >= 50 ? COLORS.positive : COLORS.negative,
      benchmark: getBenchmark('gross_margin', 60)
    },
    { 
      metric: 'Operating Margin', 
      value: profitability?.operating_margin || 0, 
      // Red for negative or low (<15%), green for healthy (>=15%)
      color: (profitability?.operating_margin || 0) >= 15 ? COLORS.positive : COLORS.negative,
      benchmark: getBenchmark('operating_margin', 20)
    },
    { 
      metric: 'EBITDA Margin',
      value: profitability?.ebitda_margin || 0,
      // Red for negative or low (<15%), green for healthy (>=15%)
      color: (profitability?.ebitda_margin || 0) >= 15 ? COLORS.positive : COLORS.negative,
      benchmark: 20 // EBITDA not in benchmark object, use default
    },
    { 
      metric: 'Net Margin', 
      value: profitability?.net_margin || 0, 
      // Red for negative, green for positive
      color: (profitability?.net_margin || 0) > 0 ? COLORS.positive : COLORS.negative,
      benchmark: getBenchmark('net_margin', 10)
    }
  ];

  // Check if profitability data has meaningful values (not all zeros)
  const hasMeaningfulProfitabilityData = profitabilityData.some(item => item.value !== 0);

  // Data availability flags for hiding empty sections
  const hasRevenueData = !!(revenueByService || revenueByGeography);
  const hasProfitabilityData = !!(profitability && hasMeaningfulProfitabilityData);
  
  // Cost comparison with benchmark - dynamic benchmarks
  const getCostBenchmark = (field, defaultValue) => {
    // Handle both object and string formats for backward compatibility
    if (cost_structure?.industry_benchmark) {
      if (typeof cost_structure.industry_benchmark === 'object') {
        return cost_structure.industry_benchmark[field] || defaultValue;
      }
    }
    return defaultValue;
  };

  const costComparisonData = [
    {
      category: 'Personnel Costs',
      actual: cost_structure?.personnel_percentage || 0,
      benchmark: getCostBenchmark('personnel', 75),
      color: COLORS.cost[0]
    },
    {
      category: 'Overtime',
      actual: cost_structure?.overtime_percentage || 0,
      benchmark: getCostBenchmark('overtime', 5), // Typical overtime 3-7% of revenue
      color: '#f59e0b'
    },
    {
      category: 'Admin Overhead',
      actual: cost_structure?.admin_overhead_percentage || 0,
      benchmark: getCostBenchmark('admin', 10),
      color: COLORS.cost[1]
    },
    {
      category: 'Other Costs',
      actual: cost_structure?.other_costs_percentage || 0,
      benchmark: 15, // Not in benchmark object, use default
      color: COLORS.cost[2]
    }
  ].filter(item => item.actual > 0);

  // More data availability flags
  const hasCostData = !!(costStructureData?.length > 0 || costComparisonData?.length > 0);
  const hasLiquidityData = !!(balance_sheet?.total_assets || balance_sheet?.current_ratio || balance_sheet?.debt_breakdown?.length > 0);
  const hasTrendsData = !!(trends?.revenue_over_time?.length >= 2 || trends?.profit_over_time?.length >= 2);
  
  // Check if ANY chart section has data
  const hasAnyChartData = hasRevenueData || hasProfitabilityData || hasCostData || hasLiquidityData || hasTrendsData;

  // Enhanced KPI Cards with sparklines
  const kpiCards = [
    {
      icon: <DollarSign size={24} />,
      label: 'Total Revenue',
      value: formatCurrency(revenue_analysis?.total_revenue?.amount, true), // Compact format (e.g., €14.6M)
      trend: null,
      sparklineData: null,
      color: 'purple'
    },
    {
      icon: profitability?.net_margin >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />,
      label: 'Net Margin', // Fixed formatting
      value: formatPercent(profitability?.net_margin),
      trend: null,
      sparklineData: null,
      color: profitability?.net_margin < 0 ? 'red' : 'green'
    },
    {
      icon: <Users size={24} />,
      label: 'Headcount',
      value: operational_metrics?.headcount || 'Not available in source',
      trend: null,
      sparklineData: null,
      color: 'blue'
    },
    {
      icon: <Calendar size={24} />,
      label: 'DSO',
      value: operational_metrics?.dso_days ? `${operational_metrics.dso_days} days` : 'Not available in source',
      trend: null,
      sparklineData: null,
      color: 'pink'
    },
    {
      icon: <DollarSign size={24} />,
      label: 'Working Capital',
      value: balance_sheet?.working_capital ? formatCurrency(balance_sheet.working_capital, true) : 'Not available',
      trend: null,
      sparklineData: null,
      color: (balance_sheet?.working_capital || 0) > 0 ? 'green' : 'red',
      subtitle: balance_sheet?.working_capital && revenue_analysis?.total_revenue?.amount 
        ? `${((balance_sheet.working_capital / revenue_analysis.total_revenue.amount) * 100).toFixed(1)}% of revenue`
        : null
    },
    {
      icon: <TrendingUp size={24} />,
      label: 'Cash Cycle',
      value: operational_metrics?.cash_conversion_cycle !== null && operational_metrics?.cash_conversion_cycle !== undefined
        ? `${operational_metrics.cash_conversion_cycle} days` 
        : 'Not available',
      trend: null,
      sparklineData: null,
      color: (operational_metrics?.cash_conversion_cycle || 999) < 45 ? 'green' : (operational_metrics?.cash_conversion_cycle || 999) < 90 ? 'pink' : 'red',
      subtitle: 'Time to convert sales to cash'
    }
  ];

  // Custom theme for Nivo charts
  const nivoTheme = {
    background: 'transparent',
    textColor: COLORS.chart.text,
    fontSize: 12,
    fontFamily: 'Inter, system-ui, sans-serif',
    axis: {
      domain: {
        line: {
          stroke: COLORS.chart.grid,
          strokeWidth: 1
        }
      },
      ticks: {
        line: {
          stroke: COLORS.chart.grid,
          strokeWidth: 1
        },
        text: {
          fill: COLORS.chart.text,
          fontSize: 11,
          fontWeight: 500
        }
      },
      legend: {
        text: {
          fill: COLORS.chart.text,
          fontSize: 12,
          fontWeight: 600
        }
      }
    },
    grid: {
      line: {
        stroke: COLORS.chart.grid,
        strokeWidth: 1,
        strokeDasharray: '4 4'
      }
    },
    legends: {
      text: {
        fill: COLORS.chart.text,
        fontSize: 11
      }
    },
    tooltip: {
      container: {
        background: COLORS.chart.tooltip.bg,
        color: COLORS.chart.tooltip.text,
        fontSize: 13,
        fontWeight: 500,
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
        padding: '14px 18px',
        border: `1px solid ${COLORS.chart.tooltip.border}`,
        backdropFilter: 'blur(10px)'
      }
    }
  };

  // Enhanced tooltip component
  const EnhancedTooltip = ({ datum, showBenchmark = false, benchmark = null }) => (
    <div style={{
      background: COLORS.chart.tooltip.bg,
      color: COLORS.chart.tooltip.text,
      padding: '14px 18px',
      borderRadius: '12px',
      border: `2px solid ${datum.color || COLORS.neutral}`,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(10px)',
      minWidth: '200px'
    }}>
      <div style={{ 
        fontWeight: 700, 
        fontSize: '15px', 
        marginBottom: '8px',
        borderBottom: `1px solid ${COLORS.chart.grid}`,
        paddingBottom: '6px'
      }}>
        {datum.id || datum.indexValue}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <span style={{ 
          display: 'inline-block',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: datum.color || COLORS.neutral
        }}></span>
        <span style={{ fontWeight: 600 }}>{formatCurrency(datum.value)}</span>
      </div>
      {datum.data?.percentage && (
        <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>
          {datum.data.percentage.toFixed(1)}% of total
        </div>
      )}
      {showBenchmark && benchmark && (
        <div style={{ 
          marginTop: '8px', 
          paddingTop: '8px', 
          borderTop: `1px solid ${COLORS.chart.grid}`,
          fontSize: '12px',
          color: '#cbd5e1'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span>Benchmark:</span>
            <span style={{ fontWeight: 600, color: COLORS.benchmark }}>{benchmark}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span>Difference:</span>
            <span style={{ 
              fontWeight: 600, 
              color: datum.value > benchmark ? COLORS.positive : COLORS.negative 
            }}>
              {datum.value > benchmark ? '+' : ''}{(datum.value - benchmark).toFixed(1)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Mini Sparkline Component for KPI Cards
  const MiniSparkline = ({ data, color }) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div style={{ height: '45px', marginTop: '6px', marginBottom: '-2px' }}>
        <ResponsiveLine
          data={data}
          margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
          xScale={{ type: 'linear', min: 0, max: 11 }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          curve="monotoneX"
          enablePoints={false}
          enableGridX={false}
          enableGridY={false}
          enableArea={true}
          areaOpacity={0.25}
          lineWidth={2.5}
          colors={[color]}
          isInteractive={false}
          theme={{
            ...nivoTheme,
            axis: { ticks: { text: { fill: 'transparent' } } }
          }}
        />
      </div>
    );
  };

  return (
    <>
      {/* Floating Charts Button - only show when not externally controlled */}
      {externalOpen === null && (
        <button 
          className="fixed bottom-20 right-[calc(1.25rem+220px+4rem)] flex items-center justify-center w-[50px] h-[50px] p-0 bg-[rgba(0,12,20,0.98)] backdrop-blur-xl border border-violet-500/30 rounded-xl text-violet-500 cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.3)] transition-all duration-200 z-dropdown hover:bg-violet-500/[0.15] hover:border-violet-500/50 hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(0,0,0,0.4)] active:translate-y-0 group max-md:bottom-20 max-md:right-4 max-md:w-[38px] max-md:h-[38px]"
          onClick={() => setDrawerOpen(!drawerOpen)}
          title={t('viewCharts') || 'View Charts'}
        >
          <BarChart3 size={18} />
          <span className="absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 px-2.5 py-1.5 bg-black/90 rounded-md text-[0.7rem] font-medium text-content whitespace-nowrap opacity-0 invisible transition-all duration-150 pointer-events-none group-hover:opacity-100 group-hover:visible">Charts</span>
        </button>
      )}

      {/* Charts Drawer Overlay */}
      <div className={`fixed top-[72px] left-0 right-0 bottom-0 z-modal transition-all duration-400 ${drawerOpen ? 'pointer-events-auto opacity-100 visible' : 'pointer-events-none opacity-0 invisible'}`}>
        <div 
          className={`absolute top-0 left-0 w-full h-full transition-all duration-400 ${drawerOpen ? 'bg-surface-overlay backdrop-blur-sm' : 'bg-transparent'}`} 
          onClick={() => setDrawerOpen(false)}
        ></div>
        
        <div className={`absolute top-0 right-0 w-[90%] max-w-[1400px] h-full bg-slate-900 shadow-[-4px_0_32px_rgba(0,0,0,0.3)] flex flex-col overflow-hidden will-change-transform transition-transform duration-400 max-md:w-full max-md:max-w-full ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between gap-4 px-10 py-8 bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-b border-default max-md:px-4 max-md:py-6">
            <h2 className="flex items-center gap-4 m-0 font-display text-[1.75rem] font-bold text-content bg-gradient-to-br from-indigo-500 to-violet-500 bg-clip-text text-transparent max-md:text-xl flex-1">
              <BarChart3 size={28} />
              {t('financialVisualizations') || 'Financial Visualizations'}
            </h2>
            <button 
              className="flex items-center justify-center w-14 h-14 border-2 border-status-error/60 bg-slate-800/90 rounded-xl text-content cursor-pointer transition-all duration-200 hover:bg-status-error hover:border-status-error hover:scale-110 hover:shadow-2xl shadow-xl flex-shrink-0 relative z-10"
              onClick={() => setDrawerOpen(false)}
              title="Close Charts"
              aria-label="Close Charts"
            >
              <X size={28} strokeWidth={3} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-10 max-md:p-6">
            <div className="w-full my-8 flex flex-col gap-8">
              {/* Enhanced KPI Cards with Sparklines */}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-5 mb-8 max-md:grid-cols-1">
                {kpiCards.map((kpi, index) => {
                  const colorClasses = {
                    purple: 'bg-gradient-to-br from-violet-500/10 to-indigo-500/5 border-violet-500/30 before:bg-gradient-to-b before:from-violet-500 before:to-indigo-500',
                    green: 'bg-gradient-to-br from-emerald-500/10 to-green-600/5 border-emerald-500/30 before:bg-gradient-to-b before:from-emerald-500 before:to-green-600',
                    red: 'bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30 before:bg-gradient-to-b before:from-red-500 before:to-red-600',
                    blue: 'bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30 before:bg-gradient-to-b before:from-blue-500 before:to-blue-600',
                    pink: 'bg-gradient-to-br from-pink-500/10 to-pink-600/5 border-pink-500/30 before:bg-gradient-to-b before:from-pink-500 before:to-pink-600',
                  };
                  return (
                    <div 
                      key={index} 
                      className={`px-6 py-5 rounded-2xl border flex items-center gap-4 relative overflow-hidden min-h-[110px] h-[110px] backdrop-blur-xl before:content-[''] before:absolute before:top-0 before:left-0 before:w-1 before:h-full animate-fade-in-up ${colorClasses[kpi.color] || colorClasses.purple}`}
                      style={{ animationDelay: `${0.05 * (index + 1)}s` }}
                    >
                      <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-brand/10 text-primary flex-shrink-0">{kpi.icon}</div>
                      <div className="flex-1 flex flex-col gap-1 justify-center">
                        <div className="text-xs text-content-secondary font-medium uppercase tracking-wider leading-tight">{kpi.label}</div>
                        <div className="text-xl font-bold text-content font-display leading-tight">{kpi.value}</div>
                        {kpi.subtitle && (
                          <div className="text-xs text-slate-400 mt-1 font-medium">{kpi.subtitle}</div>
                        )}
                        {kpi.trend !== undefined && kpi.trend !== null && (
                          <div className={`text-[0.6875rem] font-semibold flex items-center gap-1 mt-0.5 ${kpi.trend >= 0 ? 'text-emerald-500' : 'text-status-error'}`}>
                            {kpi.trend >= 0 ? '↑' : '↓'} {Math.abs(kpi.trend).toFixed(1)}% YoY
                          </div>
                        )}
                        {kpi.sparklineData && (
                          <MiniSparkline 
                            data={kpi.sparklineData} 
                            color={kpi.trend >= 0 ? COLORS.positive : COLORS.negative}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* No Charts Available Message */}
              {!hasAnyChartData && (
                <div className="p-12 text-center rounded-2xl mb-8 bg-gradient-to-br from-amber-500/5 to-amber-600/[0.02] border border-amber-500/20 backdrop-blur-xl">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500">
                    <BarChart3 size={48} />
                  </div>
                  <h3 className="text-2xl font-semibold text-amber-500 mb-4">Limited Financial Data for Charts</h3>
                  <p className="text-base text-content-secondary max-w-[600px] mx-auto mb-6 leading-relaxed">
                    The uploaded document didn't contain enough structured numerical data to generate detailed charts.
                    This can happen with qualitative reports, strategy documents, or when financial tables aren't clearly formatted.
                  </p>
                  <div className="bg-surface-primary rounded-xl p-6 max-w-[500px] mx-auto text-left">
                    <strong className="block text-emerald-500 text-sm mb-3">What you can still do:</strong>
                    <ul className="list-none p-0 m-0">
                      <li className="text-sm text-content-secondary py-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-emerald-500">Review the Executive Summary above for key insights</li>
                      <li className="text-sm text-content-secondary py-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-emerald-500">Check the Critical Alerts and Required Actions sections</li>
                      <li className="text-sm text-content-secondary py-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-emerald-500">Use the Chat feature to ask specific questions about the document</li>
                      <li className="text-sm text-content-secondary py-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-emerald-500">Try uploading a more detailed financial statement (P&L, Balance Sheet)</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Revenue Charts Section */}
              {hasRevenueData && (
                <div className="p-8 rounded-3xl border border-subtle bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl animate-fade-in-up max-md:p-6">
                  <div 
                    className="flex justify-between items-center mb-6 cursor-pointer select-none pb-4 border-b border-default"
                    onClick={() => toggleSection('revenue')}
                  >
                    <h3 className="font-display text-2xl font-bold text-content m-0 tracking-wide max-md:text-xl">{t('revenueBreakdown')}</h3>
                    {expandedSections.revenue ? <ChevronUp size={20} className="text-primary transition-transform duration-200 hover:scale-110" /> : <ChevronDown size={20} className="text-primary transition-transform duration-200 hover:scale-110" />}
                  </div>
                  
                  {expandedSections.revenue && (
                    <>
                      {/* Revenue by Service Line - Full Width Row */}
                      {revenueByService && (
                        <div className="w-full flex flex-col gap-4">
                          <h4 className="font-display text-lg font-semibold text-content m-0 mb-2 text-center">{t('byServiceLine')}
                            <span style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: 'normal', 
                              color: '#94a3b8',
                              marginLeft: '1rem',
                              fontStyle: 'italic'
                            }}>
                              (Revenue Distribution %)
                            </span>
                          </h4>
                          <div style={{ height: '400px' }}>
                            <ResponsivePie
                              data={revenueByService}
                              margin={{ top: 20, right: 40, bottom: 40, left: 40 }}
                              innerRadius={0.65}
                              padAngle={2}
                              cornerRadius={6}
                              activeOuterRadiusOffset={12}
                              colors={{ datum: 'data.color' }}
                              borderWidth={3}
                              borderColor="#1e293b"
                              arcLinkLabelsSkipAngle={10}
                              arcLinkLabelsTextColor={COLORS.chart.text}
                              arcLinkLabelsThickness={3}
                              arcLinkLabelsColor={{ from: 'color' }}
                              arcLabelsSkipAngle={15}
                              arcLabel={d => `${d.data.percentage.toFixed(1)}%`}
                              arcLabelsTextColor="#ffffff"
                              arcLabelsRadiusOffset={0.55}
                              theme={nivoTheme}
                              tooltip={(props) => <EnhancedTooltip datum={props.datum} />}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Service Line Profitability Comparison - Full Width Row */}
                      {revenue_analysis?.by_service_line?.length >= 2 && (() => {
                        // Note: chart-wrapper-full replaced with Tailwind classes above
                        // Check if service line data includes meaningful profitability metrics (not just zeros)
                        const hasServiceLineProfitability = revenue_analysis.by_service_line.some(
                          line => (line.margin !== undefined && Math.abs(line.margin) > 0.1) || 
                                  (line.profit !== undefined && Math.abs(line.profit) > 0)
                        );
                        
                        if (!hasServiceLineProfitability) {
                          return (
                            <div className="w-full flex flex-col gap-4" style={{ marginTop: '2rem' }}>
                              <h4>Service Line Profitability Comparison</h4>
                              <div style={{ 
                                height: '360px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: '1.5rem',
                                padding: '3rem 2rem',
                                textAlign: 'center',
                                background: 'rgba(30, 41, 59, 0.4)',
                                borderRadius: '16px',
                                border: '2px dashed rgba(148, 163, 184, 0.3)'
                              }}>
                                <AlertTriangle size={64} style={{ color: '#f59e0b', opacity: 0.8 }} />
                                <div style={{ color: '#e2e8f0', maxWidth: '500px' }}>
                                  <strong style={{ fontSize: '1.125rem', display: 'block', marginBottom: '0.75rem', color: '#ffffff' }}>
                                    Service-line P&L breakdown not available
                                  </strong>
                                  <p style={{ fontSize: '0.9375rem', lineHeight: '1.6', opacity: 0.9 }}>
                                    Revenue distribution by service line is available, but profitability metrics (margins, profits) per service line are not included in the source documents.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // If we have profitability data, show comparison chart - FULL WIDTH
                        const serviceProfitData = revenue_analysis.by_service_line.map((line) => ({
                          service: line.service,
                          margin: line.margin || 0,
                          // Color based on profitability: red for negative, green for positive
                          color: (line.margin || 0) < 0 ? COLORS.negative : COLORS.positive
                        }));
                        
                        return (
                          <div className="w-full flex flex-col gap-4" style={{ marginTop: '2rem' }}>
                            <h4>Service Line Profitability Comparison
                              <span style={{ 
                                fontSize: '0.875rem', 
                                fontWeight: 'normal', 
                                color: '#94a3b8',
                                marginLeft: '1rem',
                                fontStyle: 'italic'
                              }}>
                                (Profit Margins %)
                              </span>
                            </h4>
                            <div style={{ height: '400px' }}>
                              <ResponsiveBar
                                data={serviceProfitData}
                                keys={['margin']}
                                indexBy="service"
                                margin={{ top: 20, right: 40, bottom: 100, left: 100 }}
                                padding={0.3}
                                layout="vertical"
                                valueScale={{ type: 'linear' }}
                                colors={{ datum: 'data.color' }}
                                borderRadius={8}
                                borderWidth={2}
                                borderColor="#1e293b"
                                axisBottom={{
                                  tickSize: 5,
                                  tickPadding: 8,
                                  tickRotation: -45,
                                  legend: 'Service Line',
                                  legendPosition: 'middle',
                                  legendOffset: 80
                                }}
                                axisLeft={{
                                  tickSize: 5,
                                  tickPadding: 8,
                                  tickRotation: 0,
                                  format: v => `${v}%`,
                                  legend: 'Margin %',
                                  legendPosition: 'middle',
                                  legendOffset: -80
                                }}
                                labelSkipWidth={12}
                                labelSkipHeight={12}
                                labelTextColor="#ffffff"
                                label={d => `${d.value.toFixed(1)}%`}
                                theme={{
                                  axis: { 
                                    ticks: { text: { fill: '#e2e8f0', fontSize: 12 } },
                                    legend: { text: { fill: '#e2e8f0', fontSize: 14, fontWeight: 600 } }
                                  },
                                  grid: { line: { stroke: COLORS.chart.grid } }
                                }}
                                tooltip={({ indexValue, value, color }) => (
                                  <div style={{
                                    background: COLORS.chart.tooltip.bg,
                                    padding: '12px 16px',
                                    border: `2px solid ${color}`,
                                    borderRadius: '8px',
                                    color: COLORS.chart.tooltip.text,
                                    backdropFilter: 'blur(10px)'
                                  }}>
                                    <strong>{indexValue}</strong><br/>
                                    Margin: {value.toFixed(1)}%
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                        );
                      })()}

                      {/* Revenue by Geography - Full Width Row */}
                      {revenueByGeography && (
                        <div className="w-full flex flex-col gap-4" style={{ marginTop: '2rem' }}>
                          <h4>{t('byGeography')}</h4>
                          <div style={{ height: '400px' }}>
                            <ResponsiveBar
                              data={revenueByGeography}
                              keys={['revenue']}
                              indexBy="location"
                              margin={{ top: 30, right: 40, bottom: 70, left: 90 }}
                              padding={0.4}
                              colors={({ data }) => data.color}
                              borderRadius={8}
                              borderWidth={2}
                              borderColor="#1e293b"
                              valueScale={{ type: 'linear' }}
                              axisBottom={{
                                tickSize: 5,
                                tickPadding: 8,
                                tickRotation: -20,
                                legend: 'Location',
                                legendPosition: 'middle',
                                legendOffset: 55
                              }}
                              axisLeft={{
                                tickSize: 5,
                                tickPadding: 8,
                                tickRotation: 0,
                                legend: 'Revenue',
                                legendPosition: 'middle',
                                legendOffset: -70,
                                format: value => formatCurrency(value)
                              }}
                              labelSkipWidth={12}
                              labelSkipHeight={12}
                              label={d => formatCurrency(d.value)}
                              labelTextColor="#ffffff"
                              theme={nivoTheme}
                              tooltip={({ indexValue, value, color, data }) => (
                                <div style={{
                                  background: COLORS.chart.tooltip.bg,
                                  color: COLORS.chart.tooltip.text,
                                  padding: '14px 18px',
                                  borderRadius: '12px',
                                  border: `2px solid ${color}`,
                                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                                  backdropFilter: 'blur(10px)'
                                }}>
                                  <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
                                    {indexValue}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ 
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      borderRadius: '50%',
                                      backgroundColor: color
                                    }}></span>
                                    <span style={{ fontWeight: 600 }}>{formatCurrency(value)}</span>
                                  </div>
                                  {data.percentage && (
                                    <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '6px' }}>
                                      {data.percentage.toFixed(1)}% of total
                                    </div>
                                  )}
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Profitability Charts Section with Benchmarks */}
              {hasProfitabilityData && (
                <div className="p-8 rounded-3xl border border-subtle bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl animate-fade-in-up max-md:p-6">
                  <div
                    className="flex justify-between items-center mb-6 cursor-pointer select-none pb-4 border-b border-default"
                    onClick={() => toggleSection('profitability')}
                  >
                    <h3 className="font-display text-2xl font-bold text-content m-0 tracking-wide max-md:text-xl">
                      {t('profitabilityMetrics')}
                      <DataQualityBadge source={profitability?.benchmark_source} />
                    </h3>
                    {expandedSections.profitability ? <ChevronUp size={20} className="text-primary transition-transform duration-200 hover:scale-110" /> : <ChevronDown size={20} className="text-primary transition-transform duration-200 hover:scale-110" />}
                  </div>
                  {expandedSections.profitability && (
                    <>
                      {/* Margins Chart - Full Width Row */}
                      <div className="w-full flex flex-col gap-4">
                        <h4>Margins
                          <span style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: 'normal', 
                            color: '#94a3b8',
                            marginLeft: '1rem',
                            fontStyle: 'italic'
                          }}>
                            (Actual vs Industry Benchmark %)
                          </span>
                        </h4>
                        <div style={{ height: '400px' }}>
                          {hasMeaningfulProfitabilityData ? (
                          <ResponsiveBar
                            data={profitabilityData}
                            keys={['value', 'benchmark']}
                            indexBy="metric"
                            margin={{ top: 30, right: 140, bottom: 80, left: 90 }}
                            padding={0.25}
                            groupMode="grouped"
                            colors={({ id, data }) => {
                              if (id === 'benchmark') return COLORS.benchmark;
                              return data.color;
                            }}
                            borderRadius={8}
                            borderWidth={2}
                            borderColor={({ id }) => id === 'benchmark' ? '#94a3b8' : '#1e293b'}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 8,
                              tickRotation: 0,
                              legend: 'Margin Type',
                              legendPosition: 'middle',
                              legendOffset: 55
                            }}
                            axisLeft={{
                              tickSize: 5,
                              tickPadding: 8,
                              tickRotation: 0,
                              legend: 'Percentage (%)',
                              legendPosition: 'middle',
                              legendOffset: -75
                            }}
                            labelSkipWidth={12}
                            labelSkipHeight={12}
                            label={d => `${d.value.toFixed(1)}%`}
                            labelTextColor="#ffffff"
                            theme={{
                              axis: { 
                                ticks: { text: { fill: '#f1f5f9', fontSize: 12 } },
                                legend: { text: { fill: '#ffffff', fontSize: 14, fontWeight: 600 } }
                              },
                              grid: { line: { stroke: COLORS.chart.grid } },
                              legends: { text: { fill: '#ffffff' } }
                            }}
                            legends={[
                              {
                                dataFrom: 'keys',
                                anchor: 'right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 12,
                                itemWidth: 100,
                                itemHeight: 20,
                                itemDirection: 'left-to-right',
                                itemOpacity: 1,
                                symbolSize: 16,
                                symbolShape: 'circle',
                                itemTextColor: '#ffffff',
                                effects: [
                                  {
                                    on: 'hover',
                                    style: {
                                      itemTextColor: '#22d3ee'
                                    }
                                  }
                                ]
                              }
                            ]}
                            tooltip={({ id, indexValue, value, color, data }) => (
                              <div style={{
                                background: COLORS.chart.tooltip.bg,
                                color: COLORS.chart.tooltip.text,
                                padding: '14px 18px',
                                borderRadius: '12px',
                                border: `2px solid ${color}`,
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                                backdropFilter: 'blur(10px)',
                                minWidth: '200px',
                                maxWidth: '320px'
                              }}>
                                <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px', borderBottom: `1px solid ${COLORS.chart.grid}`, paddingBottom: '6px' }}>
                                  {indexValue}
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <span style={{ 
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      borderRadius: '50%',
                                      backgroundColor: color
                                    }}></span>
                                    <span style={{ fontWeight: 600 }}>{id === 'benchmark' ? 'Benchmark' : 'Actual'}: {value.toFixed(1)}%</span>
                                  </div>
                                </div>
                                {indexValue === 'EBITDA Margin' && id === 'value' && (
                                  <div style={{ 
                                    paddingTop: '8px', 
                                    borderTop: `1px solid ${COLORS.chart.grid}`,
                                    fontSize: '11px',
                                    color: '#94a3b8',
                                    fontStyle: 'italic',
                                    lineHeight: '1.4'
                                  }}>
                                    ℹ️ Calculated as Operating Income + Depreciation & Amortization
                                  </div>
                                )}
                                {id === 'value' && data.benchmark && (
                                  <div style={{ 
                                    paddingTop: '8px', 
                                    borderTop: `1px solid ${COLORS.chart.grid}`,
                                    fontSize: '12px',
                                    color: '#cbd5e1'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                      <span>vs Benchmark:</span>
                                      <span style={{ 
                                        fontWeight: 600, 
                                        color: value > data.benchmark ? COLORS.positive : COLORS.negative 
                                      }}>
                                        {value > data.benchmark ? '+' : ''}{(value - data.benchmark).toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          />
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-8 text-center bg-surface-primary border-2 border-dashed border-default rounded-xl text-content-muted [&_svg]:text-amber-500/50 [&_svg]:mb-4 [&_p]:text-base [&_p]:font-semibold [&_p]:text-content-secondary [&_p]:m-0 [&_p]:mb-2 [&_span]:text-[0.85rem] [&_span]:text-content-muted">
                              <AlertTriangle size={32} />
                              <p>No margin data available</p>
                              <span>Profitability metrics were not found in the document</span>
                            </div>
                          )}
                        </div>
                        {profitability.trends && (
                          <div className="mt-4 p-4 bg-indigo-500/5 border-l-[3px] border-brand rounded-md text-sm text-content-secondary leading-relaxed [&_strong]:text-content [&_strong]:font-semibold">
                            <strong>Trend:</strong> {profitability.trends}
                            {profitability.benchmark_source && profitability.benchmark_source !== 'not_available' && (
                              <>
                                {' | '}
                                <strong>Benchmark Source:</strong> {profitability.benchmark_source === 'document' ? t('documentData') : t('aiEstimate')}
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Revenue vs COGS Comparison (only if gross margin < 15%) - FULL WIDTH */}
                      {profitability?.gross_margin < 15 && revenue_analysis?.total_revenue?.amount && (
                        <div className="w-full flex flex-col gap-4" style={{ marginTop: '2rem' }}>
                          <h4>Revenue vs Cost of Sales
                            <span style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: 'normal', 
                              color: COLORS.negative,
                              marginLeft: '1rem'
                            }}>
                              (Gross Margin: {formatPercent(profitability.gross_margin)})
                            </span>
                          </h4>
                          <div style={{ height: '400px' }}>
                            <ResponsiveBar
                              data={[
                                {
                                  category: 'Financial Structure',
                                  Revenue: revenue_analysis.total_revenue.amount,
                                  'Cost of Sales': revenue_analysis.total_revenue.amount * (1 - (profitability.gross_margin / 100)),
                                }
                              ]}
                              keys={['Revenue', 'Cost of Sales']}
                              indexBy="category"
                              margin={{ top: 20, right: 180, bottom: 80, left: 90 }}
                              padding={0.6}
                              layout="vertical"
                              valueScale={{ type: 'linear' }}
                              colors={[COLORS.positive, COLORS.negative]}
                              borderRadius={8}
                              borderWidth={2}
                              borderColor="#1e293b"
                              axisBottom={{
                                tickSize: 0,
                                legend: '',
                                legendPosition: 'middle',
                                legendOffset: 60
                              }}
                              axisLeft={{
                                tickSize: 5,
                                tickPadding: 8,
                                tickRotation: 0,
                                format: v => formatCurrency(v, true),
                                legend: 'Amount',
                                legendPosition: 'middle',
                                legendOffset: -100
                              }}
                              labelSkipWidth={12}
                              labelSkipHeight={12}
                              labelTextColor="#ffffff"
                              label={d => formatCurrency(d.value, true)}
                              legends={[
                                {
                                  dataFrom: 'keys',
                                  anchor: 'top-right',
                                  direction: 'column',
                                  justify: false,
                                  translateX: 160,
                                  translateY: 0,
                                  itemsSpacing: 12,
                                  itemWidth: 140,
                                  itemHeight: 20,
                                  itemDirection: 'left-to-right',
                                  itemOpacity: 1,
                                  symbolSize: 16,
                                  symbolShape: 'circle',
                                  itemTextColor: '#e2e8f0',
                                  effects: [
                                    {
                                      on: 'hover',
                                      style: {
                                        itemTextColor: '#ffffff'
                                      }
                                    }
                                  ]
                                }
                              ]}
                              theme={{
                                axis: { 
                                  ticks: { text: { fill: '#e2e8f0', fontSize: 12 } },
                                  legend: { text: { fill: '#e2e8f0', fontSize: 14, fontWeight: 600 } }
                                },
                                grid: { line: { stroke: COLORS.chart.grid } },
                                legends: { text: { fill: '#e2e8f0' } }
                              }}
                              tooltip={({ id, value }) => (
                                <div style={{
                                  background: COLORS.chart.tooltip.bg,
                                  padding: '12px 16px',
                                  border: `2px solid ${COLORS.chart.tooltip.border}`,
                                  borderRadius: '8px',
                                  color: COLORS.chart.tooltip.text,
                                  backdropFilter: 'blur(10px)'
                                }}>
                                  <strong>{id}</strong><br/>
                                  {formatCurrency(value)}
                                </div>
                              )}
                            />
                          </div>
                          <div className="mt-4 p-4 bg-indigo-500/5 border-l-[3px] border-brand rounded-md text-sm text-content-secondary leading-relaxed [&_strong]:text-content [&_strong]:font-semibold" style={{ color: COLORS.negative }}>
                            <AlertTriangle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                            <strong>Critical:</strong> Cost of sales consumes {(100 - profitability.gross_margin).toFixed(1)}% of revenue, leaving only {formatPercent(profitability.gross_margin)} gross margin.
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Cost Structure Section with Benchmark Comparison */}
              {hasCostData && (
                <div className="p-8 rounded-3xl border border-subtle bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl animate-fade-in-up max-md:p-6">
                  <div
                    className="flex justify-between items-center mb-6 cursor-pointer select-none pb-4 border-b border-default"
                    onClick={() => toggleSection('costs')}
                  >
                    <h3 className="font-display text-2xl font-bold text-content m-0 tracking-wide max-md:text-xl">
                      {t('costStructure')}
                      <DataQualityBadge source={cost_structure?.benchmark_source} />
                    </h3>
                    {expandedSections.costs ? <ChevronUp size={20} className="text-primary transition-transform duration-200 hover:scale-110" /> : <ChevronDown size={20} className="text-primary transition-transform duration-200 hover:scale-110" />}
                  </div>
                  {expandedSections.costs && (
                    <>
                      <div className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-8 max-lg:grid-cols-1">
                        <div className="flex flex-col gap-4">
                          <h4>{t('costAllocation')}
                            <span style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: 'normal', 
                              color: '#94a3b8',
                              marginLeft: '1rem',
                              fontStyle: 'italic'
                            }}>
                              (% of Total Revenue)
                            </span>
                          </h4>
                          <div style={{ height: '360px' }}>
                            {costStructureData && costStructureData.length > 0 ? (
                            <ResponsivePie
                            data={costStructureData}
                            margin={{ top: 20, right: 180, bottom: 40, left: 40 }}
                            innerRadius={0.65}
                            padAngle={2}
                            cornerRadius={6}
                            activeOuterRadiusOffset={12}
                            colors={{ datum: 'data.color' }}
                            borderWidth={3}
                            borderColor="#1e293b"
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor={COLORS.chart.text}
                            arcLinkLabelsThickness={3}
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLabelsSkipAngle={15}
                            arcLabel={d => `${d.value.toFixed(1)}%`}
                            arcLabelsTextColor="#ffffff"
                            arcLabelsRadiusOffset={0.55}
                            theme={nivoTheme}
                            tooltip={(props) => (
                              <div style={{
                                background: COLORS.chart.tooltip.bg,
                                color: COLORS.chart.tooltip.text,
                                padding: '14px 18px',
                                borderRadius: '12px',
                                border: `2px solid ${props.datum.color}`,
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                                backdropFilter: 'blur(10px)'
                              }}>
                                <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
                                  {props.datum.label}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ 
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: props.datum.color
                                  }}></span>
                                  <span style={{ fontWeight: 600 }}>{props.datum.value.toFixed(1)}%</span>
                                </div>
                              </div>
                            )}
                            legends={[
                              {
                                anchor: 'right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 12,
                                itemWidth: 100,
                                itemHeight: 20,
                                itemTextColor: '#ffffff',
                                itemDirection: 'left-to-right',
                                itemOpacity: 1,
                                symbolSize: 16,
                                symbolShape: 'circle',
                                effects: [
                                  {
                                    on: 'hover',
                                    style: {
                                      itemTextColor: '#22d3ee'
                                    }
                                  }
                                ]
                              }
                            ]}
                          />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-8 text-center bg-surface-primary border-2 border-dashed border-default rounded-xl text-content-muted [&_svg]:text-amber-500/50 [&_svg]:mb-4 [&_p]:text-base [&_p]:font-semibold [&_p]:text-content-secondary [&_p]:m-0 [&_p]:mb-2 [&_span]:text-[0.85rem] [&_span]:text-content-muted">
                                <AlertTriangle size={32} />
                                <p>No cost allocation data available</p>
                                <span>Cost breakdown information was not found in the document</span>
                              </div>
                            )}
                        </div>
                        </div>
                      </div>
                      
                      {/* Benchmark Chart - Full Width Row */}
                      <div className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-8 max-lg:grid-cols-1">
                        <div className="w-full flex flex-col gap-4">
                          <h4>{t('costVsBenchmark')}</h4>
                          <div style={{ height: '400px' }}>
                            {costComparisonData && costComparisonData.length > 0 ? (
                            <ResponsiveBar
                              data={costComparisonData}
                              keys={['actual', 'benchmark']}
                              indexBy="category"
                              margin={{ top: 30, right: 140, bottom: 80, left: 90 }}
                            padding={0.25}
                            groupMode="grouped"
                            colors={({ id, data }) => {
                              if (id === 'benchmark') return COLORS.benchmark;
                              return data.color;
                            }}
                            borderRadius={8}
                            borderWidth={2}
                            borderColor="#1e293b"
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 8,
                              tickRotation: -20,
                              legend: 'Cost Category',
                              legendPosition: 'middle',
                              legendOffset: 65
                            }}
                            axisLeft={{
                              tickSize: 5,
                              tickPadding: 8,
                              tickRotation: 0,
                              legend: 'Percentage (%)',
                              legendPosition: 'middle',
                              legendOffset: -70
                            }}
                            labelSkipWidth={12}
                            labelSkipHeight={12}
                            label={d => `${d.value.toFixed(1)}%`}
                            labelTextColor="#ffffff"
                            theme={{
                              ...nivoTheme,
                              markers: {
                                text: {
                                  fill: '#e2e8f0',
                                  fontSize: 12,
                                  fontWeight: 600
                                }
                              }
                            }}
                            markers={
                              costComparisonData.map((item, idx) => ({
                                axis: 'y',
                                value: item.benchmark,
                                lineStyle: { 
                                  stroke: '#94a3b8', 
                                  strokeWidth: 2.5, 
                                  strokeDasharray: '8 4' 
                                },
                                legend: `Industry Avg: ${item.benchmark}%`,
                                legendOrientation: 'horizontal',
                                textStyle: {
                                  fill: '#e2e8f0',
                                  fontSize: 12,
                                  fontWeight: 600
                                }
                              }))
                            }
                            legends={[
                              {
                                dataFrom: 'keys',
                                anchor: 'right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 2,
                                itemWidth: 100,
                                itemHeight: 20,
                                itemDirection: 'left-to-right',
                                itemOpacity: 1,
                                symbolSize: 14,
                                symbolShape: 'circle',
                                itemTextColor: COLORS.chart.text
                              }
                            ]}
                            tooltip={({ id, indexValue, value, color, data }) => (
                              <div style={{
                                background: COLORS.chart.tooltip.bg,
                                color: COLORS.chart.tooltip.text,
                                padding: '14px 18px',
                                borderRadius: '12px',
                                border: `2px solid ${color}`,
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                                backdropFilter: 'blur(10px)',
                                minWidth: '220px'
                              }}>
                                <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
                                  {indexValue}
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <span style={{ 
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      borderRadius: '50%',
                                      backgroundColor: color
                                    }}></span>
                                    <span style={{ fontWeight: 600 }}>{id === 'benchmark' ? 'Benchmark' : 'Actual'}: {value.toFixed(1)}%</span>
                                  </div>
                                </div>
                                {id === 'actual' && (
                                  <div style={{ 
                                    paddingTop: '8px', 
                                    borderTop: `1px solid ${COLORS.chart.grid}`,
                                    fontSize: '12px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                      {value > data.benchmark ? (
                                        <>
                                          <AlertTriangle size={14} style={{ color: COLORS.negative }} />
                                          <span style={{ color: COLORS.negative, fontWeight: 600 }}>
                                            +{(value - data.benchmark).toFixed(1)}% above benchmark
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span style={{ color: COLORS.positive }}>✓</span>
                                          <span style={{ color: COLORS.positive, fontWeight: 600 }}>
                                            {(value - data.benchmark).toFixed(1)}% below benchmark
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-8 text-center bg-surface-primary border-2 border-dashed border-default rounded-xl text-content-muted [&_svg]:text-amber-500/50 [&_svg]:mb-4 [&_p]:text-base [&_p]:font-semibold [&_p]:text-content-secondary [&_p]:m-0 [&_p]:mb-2 [&_span]:text-[0.85rem] [&_span]:text-content-muted">
                                <AlertTriangle size={32} />
                                <p>No benchmark comparison data available</p>
                                <span>Cost category data was not found in the document</span>
                              </div>
                            )}
                        </div>
                          {cost_structure.vs_industry_benchmark && (
                            <div className="mt-4 p-4 bg-indigo-500/5 border-l-[3px] border-brand rounded-md text-sm text-content-secondary leading-relaxed [&_strong]:text-content [&_strong]:font-semibold">
                              <strong>Analysis:</strong> {cost_structure.vs_industry_benchmark}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Liquidity & Balance Sheet Section */}
              {hasLiquidityData && (
                <div className="p-8 rounded-3xl border border-subtle bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl animate-fade-in-up max-md:p-6">
                  <div 
                    className="flex justify-between items-center mb-6 cursor-pointer select-none pb-4 border-b border-default"
                    onClick={() => toggleSection('liquidity')}
                  >
                    <h3 className="font-display text-2xl font-bold text-content m-0 tracking-wide max-md:text-xl">Liquidity & Balance Sheet</h3>
                    {expandedSections.liquidity ? <ChevronUp size={20} className="text-primary transition-transform duration-200 hover:scale-110" /> : <ChevronDown size={20} className="text-primary transition-transform duration-200 hover:scale-110" />}
                  </div>
                  {expandedSections.liquidity && (
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-8 max-lg:grid-cols-1">
                      {/* Debt Maturity Breakdown */}
                      {(balance_sheet.short_term_debt || balance_sheet.long_term_debt) && (
                        <div className="flex flex-col gap-4">
                          <h4>Debt Maturity Profile
                            <span style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: 'normal', 
                              color: '#94a3b8',
                              marginLeft: '1rem',
                              fontStyle: 'italic'
                            }}>
                              (By Maturity Term)
                            </span>
                          </h4>
                          <div style={{ height: '360px' }}>
                            <ResponsivePie
                              data={[
                                {
                                  id: 'Short-term Debt',
                                  label: 'Short-term (<12 months)',
                                  value: balance_sheet.short_term_debt || 0,
                                  color: '#ef4444' // Red for short-term risk
                                },
                                {
                                  id: 'Long-term Debt',
                                  label: 'Long-term (>12 months)',
                                  value: balance_sheet.long_term_debt || 0,
                                  color: '#10b981' // Green for long-term safety
                                }
                              ].filter(item => item.value > 0)}
                              margin={{ top: 20, right: 180, bottom: 40, left: 40 }}
                              innerRadius={0.65}
                              padAngle={2}
                              cornerRadius={6}
                              activeOuterRadiusOffset={12}
                              colors={{ datum: 'data.color' }}
                              borderWidth={3}
                              borderColor="#1e293b"
                              arcLinkLabelsSkipAngle={10}
                              arcLinkLabelsTextColor={COLORS.chart.text}
                              arcLinkLabelsThickness={3}
                              arcLinkLabelsColor={{ from: 'color' }}
                              arcLabelsSkipAngle={15}
                              arcLabel={d => formatCurrency(d.value, true)}
                              arcLabelsTextColor="#ffffff"
                              arcLabelsRadiusOffset={0.55}
                              theme={nivoTheme}
                              tooltip={(props) => (
                                <div style={{
                                  background: COLORS.chart.tooltip.bg,
                                  color: COLORS.chart.tooltip.text,
                                  padding: '14px 18px',
                                  borderRadius: '12px',
                                  border: `2px solid ${props.datum.color}`,
                                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                                  backdropFilter: 'blur(10px)'
                                }}>
                                  <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
                                    {props.datum.label}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ 
                                        display: 'inline-block',
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        backgroundColor: props.datum.color
                                      }}></span>
                                      <span style={{ fontWeight: 600 }}>{formatCurrency(props.datum.value)}</span>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>
                                      {((props.datum.value / balance_sheet.total_debt) * 100).toFixed(1)}% of total debt
                                    </div>
                                  </div>
                                </div>
                              )}
                              legends={[
                                {
                                  anchor: 'right',
                                  direction: 'column',
                                  justify: false,
                                  translateX: 120,
                                  translateY: 0,
                                  itemsSpacing: 12,
                                  itemWidth: 100,
                                  itemHeight: 20,
                                  itemTextColor: '#ffffff',
                                  itemDirection: 'left-to-right',
                                  itemOpacity: 1,
                                  symbolSize: 16,
                                  symbolShape: 'circle',
                                  effects: [
                                    {
                                      on: 'hover',
                                      style: {
                                        itemTextColor: '#22d3ee'
                                      }
                                    }
                                  ]
                                }
                              ]}
                            />
                          </div>
                          {balance_sheet.cash && balance_sheet.short_term_debt && balance_sheet.short_term_debt > balance_sheet.cash && (
                            <div className="mt-4 p-4 bg-indigo-500/5 border-l-[3px] border-brand rounded-md text-sm text-content-secondary leading-relaxed [&_strong]:text-content [&_strong]:font-semibold" style={{ color: COLORS.negative, marginTop: '1rem' }}>
                              <AlertTriangle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                              <strong>Liquidity Risk:</strong> Short-term debt ({formatCurrency(balance_sheet.short_term_debt, true)}) exceeds cash ({formatCurrency(balance_sheet.cash, true)})
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Trends Over Time Section */}
              {hasTrendsData && (
                <div className="p-8 rounded-3xl border border-subtle bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl animate-fade-in-up max-md:p-6">
                  <div 
                    className="flex justify-between items-center mb-6 cursor-pointer select-none pb-4 border-b border-default"
                    onClick={() => toggleSection('trends')}
                  >
                    <h3 className="font-display text-2xl font-bold text-content m-0 tracking-wide max-md:text-xl">{t('trendsOverTime')}</h3>
                    {expandedSections.trends ? <ChevronUp size={20} className="text-primary transition-transform duration-200 hover:scale-110" /> : <ChevronDown size={20} className="text-primary transition-transform duration-200 hover:scale-110" />}
                  </div>
                  {expandedSections.trends && (
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-8 max-lg:grid-cols-1">
                      {/* Revenue Trend */}
                      {trends?.revenue_over_time?.length >= 2 && (
                        <div className="flex flex-col gap-4">
                          <h4>{t('revenue')} {t('trend')}</h4>
                          <div style={{ height: '360px' }}>
                            <ResponsiveLine
                              data={[{
                                id: 'Revenue',
                                data: filterValidPoints(trends.revenue_over_time).map(point => ({
                                  x: point.period,
                                  y: point.value
                                }))
                              }]}
                              margin={{ top: 20, right: 40, bottom: 60, left: 80 }}
                              xScale={{ type: 'point' }}
                              yScale={{
                                type: 'linear',
                                min: safeMinValue(trends.revenue_over_time) * 0.95,
                                max: 'auto',
                                stacked: false
                              }}
                              curve="monotoneX"
                              axisBottom={{
                                tickRotation: -45,
                                legend: '',
                                legendOffset: 50
                              }}
                              axisLeft={{
                                format: v => formatCurrency(v),
                                legend: t('revenue'),
                                legendPosition: 'middle',
                                legendOffset: -75,
                                tickSize: 5,
                                tickPadding: 10
                              }}
                              colors={[COLORS.revenue[0]]}
                              lineWidth={3}
                              pointSize={8}
                              pointColor={{ from: 'color' }}
                              pointBorderWidth={2}
                              pointBorderColor={{ from: 'serieColor' }}
                              enableArea={true}
                              areaOpacity={0.15}
                              areaBaselineValue={safeMinValue(trends.revenue_over_time) * 0.95}
                              useMesh={true}
                              theme={{
                                axis: { 
                                  ticks: { text: { fill: '#f1f5f9', fontSize: 12 } },
                                  legend: { text: { fill: '#ffffff', fontSize: 14, fontWeight: 600 } }
                                },
                                grid: { line: { stroke: COLORS.chart.grid } },
                                crosshair: { line: { stroke: COLORS.chart.text } }
                              }}
                              tooltip={({ point }) => (
                                <div style={{
                                  background: COLORS.chart.tooltip.bg,
                                  padding: '12px 16px',
                                  border: `2px solid ${COLORS.chart.tooltip.border}`,
                                  borderRadius: '8px',
                                  color: COLORS.chart.tooltip.text,
                                  backdropFilter: 'blur(10px)'
                                }}>
                                  <strong>{point.data.x}</strong><br/>
                                  {formatCurrency(point.data.y)}
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}

                      {/* Profit Trend */}
                      {trends?.profit_over_time?.length >= 2 && (
                        <div className="flex flex-col gap-4">
                          <h4>{t('profitabilityMetrics')} {t('trend')}</h4>
                          <div style={{ height: '360px' }}>
                            <ResponsiveLine
                              data={[{
                                id: formatMetricName(trends.profit_over_time[0]?.metric) || 'Profit',
                                data: filterValidPoints(trends.profit_over_time).map(point => ({
                                  x: point.period,
                                  y: point.value
                                }))
                              }]}
                              margin={{ top: 20, right: 40, bottom: 60, left: 80 }}
                              xScale={{ type: 'point' }}
                              yScale={{
                                type: 'linear',
                                min: safeMinValue(trends.profit_over_time) * 1.1,
                                max: 'auto',
                                stacked: false
                              }}
                              curve="monotoneX"
                              axisBottom={{
                                tickRotation: -45,
                                legend: '',
                                legendOffset: 50
                              }}
                              axisLeft={{
                                format: v => formatCurrency(v),
                                legend: formatMetricName(trends.profit_over_time[0]?.metric) || 'Profit',
                                legendPosition: 'middle',
                                legendOffset: -75,
                                tickSize: 5,
                                tickPadding: 10
                              }}
                              colors={[COLORS.profit[0]]}
                              lineWidth={3}
                              pointSize={8}
                              pointColor={{ from: 'color' }}
                              pointBorderWidth={2}
                              pointBorderColor={{ from: 'serieColor' }}
                              enableArea={true}
                              areaOpacity={0.15}
                              areaBaselineValue={safeMinValue(trends.profit_over_time) * 1.1}
                              useMesh={true}
                              theme={{
                                axis: { 
                                  ticks: { text: { fill: '#f1f5f9', fontSize: 12 } },
                                  legend: { text: { fill: '#ffffff', fontSize: 14, fontWeight: 600 } }
                                },
                                grid: { line: { stroke: COLORS.chart.grid } },
                                crosshair: { line: { stroke: COLORS.chart.text } }
                              }}
                              tooltip={({ point }) => (
                                <div style={{
                                  background: COLORS.chart.tooltip.bg,
                                  padding: '12px 16px',
                                  border: `2px solid ${COLORS.chart.tooltip.border}`,
                                  borderRadius: '8px',
                                  color: COLORS.chart.tooltip.text,
                                  backdropFilter: 'blur(10px)'
                                }}>
                                  <strong>{point.data.x}</strong><br/>
                                  {formatCurrency(point.data.y)}
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}

                      {/* Enhanced Cash Trend with Capital Injections & Runway Projection */}
                      {trends?.cash_over_time?.length >= 2 && (() => {
                        const cashData = trends.cash_over_time;
                        const hasCapitalInjections = trends.capital_injections?.length > 0;
                        
                        // Calculate cash burn rate and runway projection
                        const recentPoints = cashData.slice(-3);
                        const avgBurnRate = recentPoints.length >= 2 
                          ? (recentPoints[0].value - recentPoints[recentPoints.length - 1].value) / (recentPoints.length - 1)
                          : 0;
                        const lastCash = cashData[cashData.length - 1].value;
                        const showRunway = avgBurnRate > 0 && lastCash > 0;
                        const monthsToDepletion = showRunway ? Math.ceil(lastCash / avgBurnRate) : null;
                        
                        // Prepare data with optional projection
                        const chartData = [{
                          id: 'Cash',
                          data: cashData.map(point => ({
                            x: point.period,
                            y: point.value
                          }))
                        }];
                        
                        // Add runway projection if applicable
                        if (showRunway && monthsToDepletion > 0 && monthsToDepletion < 24) {
                          const projectionData = [];
                          for (let i = 1; i <= Math.min(monthsToDepletion + 2, 12); i++) {
                            projectionData.push({
                              x: `+${i}M`,
                              y: Math.max(0, lastCash - (avgBurnRate * i))
                            });
                          }
                          chartData.push({
                            id: 'Projected',
                            data: [
                              { x: cashData[cashData.length - 1].period, y: lastCash },
                              ...projectionData
                            ]
                          });
                        }
                        
                        return (
                          <div className="flex flex-col gap-4">
                            <h4>Cash {t('trend')}
                              {showRunway && monthsToDepletion && (
                                <span style={{ 
                                  fontSize: '0.875rem', 
                                  fontWeight: 'normal', 
                                  color: monthsToDepletion < 6 ? COLORS.negative : COLORS.chart.text,
                                  marginLeft: '1rem'
                                }}>
                                  ({monthsToDepletion} months runway)
                                </span>
                              )}
                            </h4>
                            <div style={{ height: '360px', position: 'relative' }}>
                              <ResponsiveLine
                                data={chartData}
                                margin={{ top: 30, right: 40, bottom: 60, left: 80 }}
                                xScale={{ type: 'point' }}
                                yScale={{ 
                                  type: 'linear', 
                                  min: 0,
                                  max: 'auto',
                                  stacked: false
                                }}
                                curve="monotoneX"
                                axisBottom={{
                                  tickRotation: -45,
                                  legend: '',
                                  legendOffset: 50
                                }}
                                axisLeft={{
                                  format: v => formatCurrency(v, true),
                                  legend: 'Cash',
                                  legendPosition: 'middle',
                                  legendOffset: -75,
                                  tickSize: 5,
                                  tickPadding: 10
                                }}
                                colors={[COLORS.positive, '#ef4444']}
                                lineWidth={3}
                                pointSize={8}
                                pointColor={{ from: 'color' }}
                                pointBorderWidth={2}
                                pointBorderColor={{ from: 'serieColor' }}
                                enableArea={true}
                                areaOpacity={0.15}
                                areaBaselineValue={0}
                                useMesh={true}
                                enableGridX={false}
                                legends={showRunway ? [{
                                  anchor: 'top-right',
                                  direction: 'row',
                                  translateY: -20,
                                  itemWidth: 100,
                                  itemHeight: 20,
                                  symbolSize: 12,
                                  symbolShape: (props) => props.id === 'Projected' ? (
                                    <line
                                      x1={0}
                                      y1={6}
                                      x2={12}
                                      y2={6}
                                      stroke={props.color}
                                      strokeWidth={2}
                                      strokeDasharray="4 4"
                                    />
                                  ) : (
                                    <circle cx={6} cy={6} r={6} fill={props.color} />
                                  ),
                                  effects: [{
                                    on: 'hover',
                                    style: { itemTextColor: '#fff' }
                                  }]
                                }] : []}
                                enablePointLabel={false}
                                layers={[
                                  'grid',
                                  'markers',
                                  'axes',
                                  'areas',
                                  'lines',
                                  'slices',
                                  'points',
                                  'mesh',
                                  'legends',
                                  (props) => {
                                    // Add capital injection markers
                                    if (!hasCapitalInjections) return null;
                                    return (
                                      <g>
                                        {trends.capital_injections.map((injection, idx) => {
                                          const pointIndex = cashData.findIndex(p => p.period === injection.period);
                                          if (pointIndex === -1) return null;
                                          const point = props.points.find(p => 
                                            p.serieId === 'Cash' && p.index === pointIndex
                                          );
                                          if (!point) return null;
                                          
                                          return (
                                            <g key={idx}>
                                              <line
                                                x1={point.x}
                                                y1={point.y - 40}
                                                x2={point.x}
                                                y2={point.y - 10}
                                                stroke="#10b981"
                                                strokeWidth={2}
                                                markerEnd="url(#arrow)"
                                              />
                                              <text
                                                x={point.x}
                                                y={point.y - 45}
                                                textAnchor="middle"
                                                fill="#10b981"
                                                fontSize="11"
                                                fontWeight="600"
                                              >
                                                +{formatCurrency(injection.amount, true)}
                                              </text>
                                            </g>
                                          );
                                        })}
                                        <defs>
                                          <marker
                                            id="arrow"
                                            viewBox="0 0 10 10"
                                            refX="5"
                                            refY="5"
                                            markerWidth="6"
                                            markerHeight="6"
                                            orient="auto"
                                          >
                                            <path d="M 0 0 L 10 5 L 0 10 z" fill="#10b981" />
                                          </marker>
                                        </defs>
                                      </g>
                                    );
                                  }
                                ]}
                                theme={{
                                  axis: { 
                                    ticks: { text: { fill: '#f1f5f9', fontSize: 12 } },
                                    legend: { text: { fill: '#ffffff', fontSize: 14, fontWeight: 600 } }
                                  },
                                  grid: { line: { stroke: COLORS.chart.grid } },
                                  crosshair: { line: { stroke: COLORS.chart.text } },
                                  legends: { text: { fill: '#ffffff' } }
                                }}
                                tooltip={({ point }) => (
                                  <div style={{
                                    background: COLORS.chart.tooltip.bg,
                                    padding: '12px 16px',
                                    border: `2px solid ${COLORS.chart.tooltip.border}`,
                                    borderRadius: '8px',
                                    color: COLORS.chart.tooltip.text,
                                    backdropFilter: 'blur(10px)'
                                  }}>
                                    <strong>{point.data.x}</strong><br/>
                                    {formatCurrency(point.data.y)}
                                    {point.serieId === 'Projected' && <><br/><em style={{ fontSize: '0.75rem', opacity: 0.7 }}>Projected</em></>}
                                  </div>
                                )}
                              />
                            </div>
                            {hasCapitalInjections && (
                              <div className="mt-4 p-4 bg-indigo-500/5 border-l-[3px] border-brand rounded-md text-sm text-content-secondary leading-relaxed [&_strong]:text-content [&_strong]:font-semibold" style={{ marginTop: '1rem', color: '#10b981' }}>
                                <strong>💰 Capital Injections:</strong> {trends.capital_injections.map((inj, i) => 
                                  `${inj.period}: ${formatCurrency(inj.amount, true)}`
                                ).join(' | ')}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Additional KPIs Table */}
              {operational_metrics?.other_kpis && operational_metrics.other_kpis.length > 0 && (
                <div className="p-8 rounded-3xl border border-subtle bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl animate-fade-in-up max-md:p-6">
                  <div 
                    className="flex justify-between items-center mb-6 cursor-pointer select-none pb-4 border-b border-default"
                    onClick={() => toggleSection('kpis')}
                  >
                    <h3 className="font-display text-2xl font-bold text-content m-0 tracking-wide max-md:text-xl">{t('additionalKPIs')}</h3>
                    {expandedSections.kpis ? <ChevronUp size={20} className="text-primary transition-transform duration-200 hover:scale-110" /> : <ChevronDown size={20} className="text-primary transition-transform duration-200 hover:scale-110" />}
                  </div>
                  {expandedSections.kpis && (
                    <div className="w-full overflow-x-auto mt-4">
                      <table className="w-full border-collapse text-sm">
                        <thead className="bg-indigo-500/10 border-b-2 border-indigo-500/30">
                          <tr>
                            <th className="p-4 text-left text-sm font-semibold text-content uppercase tracking-wider">Metric</th>
                            <th className="p-4 text-left text-sm font-semibold text-content uppercase tracking-wider">Value</th>
                            <th className="p-4 text-left text-sm font-semibold text-content uppercase tracking-wider">Unit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operational_metrics.other_kpis.map((kpi, index) => (
                            <tr key={index} className="transition-all duration-150 hover:bg-indigo-500/5 [&:last-child_td]:border-b-0">
                              <td className="px-4 py-3.5 border-b border-subtle text-content-secondary">{kpi.metric}</td>
                              <td className="px-4 py-3.5 border-b border-subtle font-semibold text-content text-base">{kpi.value}</td>
                              <td className="px-4 py-3.5 border-b border-subtle text-content-secondary">{kpi.unit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

// Add display name for debugging
FinancialCharts.displayName = 'FinancialCharts';

export default FinancialCharts;
